import type { Worker } from 'tesseract.js';
import { createOcrWorker } from './createOcrWorker';
import { findMvCodes } from './findMvCodes';
import { prepareImageForOcr } from './prepareImageForOcr';

const OCR_TIMEOUT_MS = 8_000;

type ScanResult =
  | { result: 'found'; code: string }
  /** No MV code in the image. */
  | { result: 'not_found' }
  /** The image contains different MV codes, so we can't tell which is right. */
  | { result: 'multiple_codes' };

type OcrOutcome =
  | ScanResult
  /** PDFs aren't scanned; they go straight to manual review. */
  | { result: 'pdf' }
  | { result: 'timeout' }
  /** OCR couldn't run, e.g. the runtime failed to load or the image is broken. */
  | { result: 'error' };

export type MvCodeOcrResult = OcrOutcome & { durationMs: number };

export function isPdf(file: File): boolean {
  return (
    file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
  );
}

let preloadedWorker: Promise<Worker> | undefined;

function takePreloadedWorker(): Promise<Worker> | undefined {
  const worker = preloadedWorker;
  preloadedWorker = undefined;
  return worker;
}

export function preloadOcrWorker(): void {
  if (preloadedWorker) return;

  const worker = createOcrWorker();
  preloadedWorker = worker;
  worker.catch(() => {
    if (preloadedWorker === worker) preloadedWorker = undefined;
  });
}

export function releasePreloadedOcrWorker(): void {
  void takePreloadedWorker()
    ?.then((worker) => worker.terminate())
    .catch(() => undefined);
}

function toScanResult(codes: string[]): ScanResult {
  const [code] = codes;

  if (!code) return { result: 'not_found' };

  return codes.length === 1
    ? { result: 'found', code }
    : { result: 'multiple_codes' };
}

/**
 * Reads the Moov MV verification code from a bank transaction screenshot.
 *
 * The code is only accepted when the image contains exactly one distinct
 * MV code (Moov repeats it in the memo). The OCR worker is always terminated,
 * including on timeout, to free its memory (~120 MB).
 */
export async function readMvCodeFromImage(
  file: File,
): Promise<MvCodeOcrResult> {
  const startedAt = performance.now();
  const withDuration = (outcome: OcrOutcome): MvCodeOcrResult => ({
    ...outcome,
    durationMs: Math.round(performance.now() - startedAt),
  });

  if (isPdf(file)) {
    return withDuration({ result: 'pdf' });
  }

  let worker: Worker | undefined;
  let isTimedOut = false;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const scan = (async (): Promise<ScanResult | null> => {
    worker = await (takePreloadedWorker() ?? createOcrWorker());
    const image = await prepareImageForOcr(file);

    if (isTimedOut) return null;

    const { data } = await worker.recognize(image);

    return toScanResult(findMvCodes(data.text));
  })();

  const timeout = new Promise<null>((resolve) => {
    timeoutId = setTimeout(() => {
      isTimedOut = true;
      resolve(null);
    }, OCR_TIMEOUT_MS);
  });

  const terminate = () => void worker?.terminate().catch(() => undefined);

  try {
    return withDuration(
      (await Promise.race([scan, timeout])) ?? {
        result: 'timeout',
      },
    );
  } catch {
    return withDuration({ result: 'error' });
  } finally {
    clearTimeout(timeoutId);

    if (worker) {
      terminate();
    } else {
      // Timed out while the worker was still loading: terminate once it exists.
      void scan.catch(() => null).finally(terminate);
    }
  }
}
